import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CurrencyContext = createContext(null);

const CURRENCIES = [
  { code: 'EUR', symbol: '€', label: 'Euro' },
  { code: 'USD', symbol: '$', label: 'US Dollar' },
  { code: 'RUB', symbol: '₽', label: 'Russian Rouble' },
  { code: 'GBP', symbol: '£', label: 'British Pound' },
];

const STORAGE_KEY = 'penlab-currency';

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyRaw] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && CURRENCIES.find(c => c.code === saved)) return saved;
    } catch (e) {}
    return 'EUR';
  });
  const [rates, setRates] = useState({ EUR: 1, USD: 1.08, RUB: 102, GBP: 0.85 });
  const [updatedAt, setUpdatedAt] = useState(null);

  const loadRates = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/currency/rates`);
      if (r.data?.rates) {
        setRates(r.data.rates);
        setUpdatedAt(r.data.updated_at || null);
      }
    } catch (e) {
      // Silent, fallback rates already seeded in state.
    }
  }, []);

  useEffect(() => { loadRates(); }, [loadRates]);

  const setCurrency = (code) => {
    if (!CURRENCIES.find(c => c.code === code)) return;
    setCurrencyRaw(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch (e) {}
  };

  /**
   * Convert an EUR-denominated amount into the currently selected currency.
   * Returns a number. Use `formatPrice` to turn it into a localised string.
   */
  const convert = useCallback((eurAmount) => {
    const n = Number(eurAmount) || 0;
    const rate = rates[currency] || 1;
    return n * rate;
  }, [currency, rates]);

  /**
   * Locale-aware formatter, returns e.g. "€99.00", "$106.92", "₽10,098.00",
   * "£83.85". Falls back to the symbol + fixed(2) if Intl isn't available.
   */
  const formatPrice = useCallback((eurAmount, opts = {}) => {
    const converted = convert(eurAmount);
    const code = currency;
    const fractionDigits = opts.fractionDigits ?? (code === 'RUB' ? 0 : 2);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(converted);
    } catch (e) {
      const symbol = CURRENCIES.find(c => c.code === code)?.symbol || code;
      return `${symbol}${converted.toFixed(fractionDigits)}`;
    }
  }, [convert, currency]);

  const currentMeta = CURRENCIES.find(c => c.code === currency) || CURRENCIES[0];

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        currencies: CURRENCIES,
        symbol: currentMeta.symbol,
        rates,
        updatedAt,
        convert,
        formatPrice,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    // Soft fallback so components outside the provider don't crash during tests.
    return {
      currency: 'EUR',
      setCurrency: () => {},
      currencies: CURRENCIES,
      symbol: '€',
      rates: { EUR: 1, USD: 1.08, RUB: 102, GBP: 0.85 },
      updatedAt: null,
      convert: (n) => Number(n) || 0,
      formatPrice: (n) => `€${(Number(n) || 0).toFixed(2)}`,
    };
  }
  return ctx;
}
